<template>
  <div
    role="banner"
    class="global-alert"
    :class="type"
  >
    <div class="wrap">
      <div
        class="content"
        :class="{
          bold: isBold,
          'has-icon': hasCustomIcon,
          'show-default-icon': showDefaultIcon && !hasCustomIcon,
          closeable: closable
        }"
      >
        <v-lazy-image
          v-if="hasCustomIcon"
          class="icon"
          :src="icon.source"
          :alt="icon.alt"
        />
        <span v-dompurify-html="content" />
      </div>
      <button
        v-if="closable"
        class="close-icon"
        aria-label="$t('reusable.close')"
        @click="closeAlert()"
        @keyup.enter="closeAlert()"
      />
    </div>
  </div>
</template>

<script>
  import VLazyImage from 'v-lazy-image'

  export default {
    components: {
      VLazyImage
    },
    props: {
      content: {
        type: String,
        required: true
      },
      type: {
        type: String,
        required: true
      },
      icon: {
        type: Object,
        default: () => {},
        required: false
      },
      closable: {
        type: Boolean,
        default: false
      },
      isBold: {
        type: Boolean,
        default: false
      },
      showDefaultIcon: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      hasCustomIcon() {
        return !!this.icon?.source
      }
    },
    methods: {
      closeAlert() {
        this.$store.commit('header/setGlobalAlert', { content: '' })
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .global-alert {
    position: relative;
    font-family: $base-font-family;

    > .wrap {
      position: relative;
      width: auto;
      max-width: $desktop-wide-width;

      @include bp-mobile {
        padding: 0 23px;
      }

      @include bp-tablet {
        max-width: $tablet-wide-width;
      }

      @include bp-desktop-small {
        padding: 0 26px;
      }
    }

    .content {
      position: relative;
      margin: 0;
      padding: 16px 0;
      font-size: em(14);
      line-height: 18px;
      color: $blue-dark;

      a,
      ::v-deep a {
        color: $blue-medium;
        font-weight: normal;

        @include outline-focus;
      }

      ::v-deep p {
        margin: 0;
      }

      @include bp-mf-tablet {
        padding: 12px 100px 14px 24px;
      }

      @include bp-mf-desktop {
        padding: 16px 100px 17px 0;
      }

      &.show-default-icon {
        padding-left: 40px;

        @include bp-mf-tablet {
          padding-left: 70px;
        }

        @include bp-mf-desktop {
          padding-left: 47px;
        }

        &:before {
          content: $icon-alert-thin;
          @include icon-font-thin();
          position: absolute;
          top: calc(50% - 13px);
          left: 0;
          font-size: 24px;
          line-height: 26px;
          color: $red-darker;

          @include bp-tablet-only {
            left: 26px;
          }
        }
      }

      &.closeable {
        padding-right: 56px;
      }

      &.has-icon {
        display: flex;
        align-items: flex-start;

        img {
          margin-right: 20px;
        }
      }

      &.bold {
        font-weight: 500;

        &:before {
          font-weight: 700;
        }
      }
    }

    &.danger {
      background-color: $danger-light;
    }

    &.warning {
      background-color: $warning-light;
    }

    &.info {
      background-color: $info-light;
    }

    &.success {
      background-color: $success-light;
    }

    .close-icon {
      display: flex;
      flex-direction: center;
      align-items: center;
      justify-content: center;

      width: 40px;
      height: 40px;

      position: absolute;
      right: 17px;
      top: calc(50% - 20px);
      color: $grey;
      cursor: pointer;

      @include outline-focus;

      &:after {
        content: $icon-close-thin;
        @include icon-font-thin();
      }

      &:hover {
        color: $blue-medium;
      }

      @include bp-tablet-only {
        right: 20px;
      }

      @include bp-mf-desktop {
        right: 0;
      }

      @include bp-desktop-small {
        right: 20px;
      }
    }
  }
</style>
